import { InterfaceJobFolderOptions, ProfileHeading } from '@components/templates/cv/sections/ProfileHeading';
import { RecruiterCallHistorySection } from '@components/features/recruiter-call';
import {
  CvDetailAvailableOperationsCurrentStatus,
  CvDetailObject,
  CvDetailRegisteredActions,
} from '@components/templates/cv/interface/CvDetailInterface';
import { ProfileSkills } from '@components/templates/cv/sections/ProfileSkills';
import { ProfileExperience } from '@components/templates/cv/sections/ProfileExperience';
import { ProfileAbout } from '@components/templates/cv/sections/ProfileAbout';
import { ProfileStudies } from '@components/templates/cv/sections/ProfileStudies';
import { ProfilePreferences } from '@components/templates/cv/sections/ProfilePreferences';
import { TagCloud } from '@components/templates/cv/sections/TagCloud';
import { ProfileAgentData } from '@components/templates/cv/sections/ProfileAgentData';
import { EvaluateBia } from '@components/templates/cv/sections/EvaluateBia';
import { ProfileComments } from '@components/templates/cv/sections/ProfileComments';
import { ProfileInterview } from '@components/templates/cv/sections/ProfileInterview';
import { ProfileRequirementAnswers } from '@components/templates/cv/sections/ProfileRequirementAnswers';
import { ProfileApplications } from '@components/templates/cv/sections/ProfileApplications';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';
import { VideoGalleryModal } from '@components/templates/cv/partials/modals/VideoGalleryModal';
import { ProfileAttachments } from '@components/templates/cv/sections/ProfileAttachments';
import { EmptyEvaluateBia } from '@components/templates/cv/sections/EmptyEvaluateBia';
// import { ProjectStatusSidebar } from '@components/features/screening/ProjectStatusSidebar';

interface IProps {
  cvDetail: CvDetailObject;
  applicantSeenContactStatus: boolean;
  cvCurrentJobUnlockFolder: InterfaceJobFolderOptions;
  phoneHighlight: boolean;
  currentCVStatus: CvDetailAvailableOperationsCurrentStatus | undefined;
  jobFolderSlug?: string;
  jobFolderInfo?:InterfaceJobFolderOptions;
  jobSlug?: string,
  openLinksInNewTab?: boolean;
  queryParams?: string;
  isCompany?: boolean;
  locale: Locale;
  jobFolderHighlight: boolean;
  revalidateCv?: (onSuccessCallback?: (cv: CvDetailObject) => void) => void;
  onDownloadCv?: () => void;
  cvActions?: CvDetailRegisteredActions;
}

export const ProfileResumeSections = ({
  cvDetail, jobFolderSlug, currentCVStatus, applicantSeenContactStatus, cvCurrentJobUnlockFolder,
  phoneHighlight, jobSlug, jobFolderHighlight, jobFolderInfo, openLinksInNewTab = false, queryParams, isCompany, locale, revalidateCv,
  onDownloadCv, cvActions,
}: IProps) => {
  const resumeSearchObject = Object.fromEntries(new URLSearchParams(queryParams));

  const DEFAULT_OWN_CV = cvDetail?.isOwnCV;
  const { isAuth } = useAuthClient();

  const hasJobInterviews = cvDetail?.jobInterviewVideos && cvDetail?.jobInterviewVideos.length > 0;
  const hasCvInterviews = cvDetail?.interviewVideos && cvDetail?.interviewVideos.length > 0;
  const hasInterviews = hasJobInterviews || hasCvInterviews;

  const hasEvaluation = !!cvDetail?.application?.evaluation || cvDetail.evaluation;
  const showBiaCol = !DEFAULT_OWN_CV && isAuth && hasEvaluation;
  const showEmptyBiaCol = !DEFAULT_OWN_CV && isAuth && !hasEvaluation;

  // Apply the same display rules for the validate link as the validate button in UnblockActions
  const eligibleForInterviewRequest = cvDetail?.application?.eligibleForInterviewRequest;
  const interviewRequestStatus = cvDetail?.application?.interviewRequest?.status;
  const shouldHideValidateLink = (eligibleForInterviewRequest === false && interviewRequestStatus == null)
    || cvDetail?.application?.interviewRequest !== undefined;
  const showValidateLink = !shouldHideValidateLink;

  const handleValidateScroll = () => {
    const element = document.getElementById('cvDetailValidateButton');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      // After scrolling, if it's a button we might want to trigger a click or highlight it
      // But user said "behave the same way", let's see if we should trigger click
      element.click();
    }
  };

  return (
    <section>
      <div className="flex flex-col gap-8 rounded-md bg-surface pb-2 pt-5 lg:grid lg:grid-cols-12 lg:justify-center">
        {/* <div className="col-span-3">
          <ProjectStatusSidebar />
        </div> */}
        {!showBiaCol && !showEmptyBiaCol && (
          <div className="col-span-6 border-r border-dotted pr-4">
            <ProfileHeading
              applicantSeenContactStatus={applicantSeenContactStatus}
              jobFolderSlug={jobFolderSlug ?? ''}
              phoneHighlight={phoneHighlight}
              cvDetail={cvDetail}
              currentCVStatus={currentCVStatus}
              openLinksInNewTab={openLinksInNewTab}
              isCompany={isCompany}
              locale={locale}
              onDownloadCv={onDownloadCv}
            />

            {!DEFAULT_OWN_CV && isAuth && (
              <div className="my-2 border-b border-dotted pb-5">
                <ProfileComments
                  cvDetail={cvDetail}
                  locale={locale}
                  jobSlug={jobSlug}
                  currentCVStatus={currentCVStatus}
                  isCompany={isCompany}
                  onAddComment={cvActions?.onAddComment}
                />
                <ProfileApplications
                  jobFolderHighlight={jobFolderHighlight}
                  cvDetail={cvDetail}
                />
              </div>
            )}

            <div className="mt-4">
              <ProfileAbout
                cvDetail={cvDetail}
                keyword={resumeSearchObject?.keyword}
              />

              <TagCloud jobInterests={cvDetail?.jobInterests} />
            </div>
          </div>
        )}

        <div className={`${(showBiaCol || showEmptyBiaCol) ? 'col-span-12 flex flex-col gap-6 lg:grid lg:grid-cols-2 lg:gap-10' : 'col-span-6 pl-4'}`}>
          {(showBiaCol || showEmptyBiaCol) && (
            <div className="order-2 w-full border-r border-dotted pr-4 lg:order-1 lg:col-span-1 lg:row-span-4">
              {/* AI evaluation and feedback (now in the center/left of the main content) */}
              {showBiaCol ? (
                <EvaluateBia cvDetail={cvDetail} onReportEvaluation={cvActions?.onReportEvaluation} />
              ) : (
                <EmptyEvaluateBia onValidateClick={handleValidateScroll} showValidateLink={showValidateLink} />
              )}
              <ProfileComments
                cvDetail={cvDetail}
                locale={locale}
                jobSlug={jobSlug}
                currentCVStatus={currentCVStatus}
                isCompany={isCompany}
                onAddComment={cvActions?.onAddComment}
              />
            </div>
          )}

          <div className={`${(showBiaCol || showEmptyBiaCol) ? 'order-1 lg:order-2 lg:col-span-1 lg:pl-6' : 'w-full'}`}>
            {(showBiaCol || showEmptyBiaCol) && (
              <div>
                <ProfileHeading
                  applicantSeenContactStatus={applicantSeenContactStatus}
                  jobFolderSlug={jobFolderSlug ?? ''}
                  phoneHighlight={phoneHighlight}
                  cvDetail={cvDetail}
                  currentCVStatus={currentCVStatus}
                  openLinksInNewTab={openLinksInNewTab}
                  isCompany={isCompany}
                  locale={locale}
                  onDownloadCv={onDownloadCv}
                />
              </div>
            )}
          </div>

          <div className={`${(showBiaCol || showEmptyBiaCol) ? 'order-3 flex-1 lg:order-3 lg:col-span-1 lg:pl-6' : 'w-full'}`}>
            {!DEFAULT_OWN_CV && (isAuth || hasInterviews) && (
              <div className="mb-2">
                <VideoGalleryModal
                  slug={cvDetail?.slug}
                  mode="list"
                  jobFolderInfo={jobFolderInfo}
                  cvDetail={cvDetail as ApiSchema<'ResumeData'>}
                  isAuth={isAuth}
                  revalidateCv={revalidateCv}
                  cvCurrentJobUnlockFolder={cvCurrentJobUnlockFolder}
                />
                <ProfileInterview cvDetail={cvDetail} jobSlug={jobSlug} locale={locale} />
                <ProfileRequirementAnswers cvDetail={cvDetail} />
                {cvDetail?.slug && (
                  <RecruiterCallHistorySection
                    candidateUserSlug={cvDetail.slug}
                    jobSlug={jobSlug ?? null}
                    className="my-6"
                  />
                )}
              </div>
            )}
            <ProfileExperience
              cvDetail={cvDetail}
              keyword={resumeSearchObject?.keyword}
            />

            <ProfileStudies
              cvDetail={cvDetail}
              keyword={resumeSearchObject?.keyword}
            />

            <ProfileSkills
              cvDetail={cvDetail}
              keyword={resumeSearchObject?.keyword}
            />

            {(showBiaCol || showEmptyBiaCol) && (
              <>
                <ProfileAbout
                  cvDetail={cvDetail}
                  keyword={resumeSearchObject?.keyword}
                />

                <TagCloud jobInterests={cvDetail?.jobInterests} />
              </>
            )}

            <ProfilePreferences
              cvDetail={cvDetail}
            />

            {!DEFAULT_OWN_CV && isAuth && cvDetail?.agent !== undefined && cvDetail?.application?.isRecommended && (
              <ProfileAgentData
                cvDetail={cvDetail}
              />
            )}

            {!cvDetail?.locked && (
              <div className="mt-2">
                <ProfileAttachments
                  cvDetail={cvDetail}
                  locale={locale}
                />
              </div>
            )}
          </div>

          {(showBiaCol || showEmptyBiaCol) && (
            <div className="order-4 lg:order-4 lg:col-span-1 lg:pl-6">
              <ProfileApplications
                jobFolderHighlight={jobFolderHighlight}
                cvDetail={cvDetail}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
